import { useRouter } from 'next/router';

import { useAuth } from '../components/AuthProvider';

import { useGetListingDetails } from './queries/properties';

/**
 * Paths that enable full screen wizard view
 * These paths hide the navigation and show a streamlined wizard experience
 */
const LISTING_VIEW_PATTERN = /^\/properties\/[^/]+\/listing$/;
const WIZARD_VIEW_PATTERNS = [
  LISTING_VIEW_PATTERN,
  /^\/properties\/[^/]+\/metrics$/,
  /^\/properties\/[^/]+\/create-listing$/,
  /^\/feature-launch-notification-banner-preview$/,
  /^\/id-verification$/,
];

/**
 * Hook to detect if current route should display in wizard view
 * Wizard view provides a full-screen, focused experience without navigation
 *
 * Used for:
 * - Property listing editing flow
 * - Create listing flow
 *
 * On the listing page, wizard view (nav hidden) applies to single-family listings;
 * multifamily listings show the nav. Stays in wizard view until isMultifamily is known.
 *
 * @returns true if current path matches wizard view patterns
 */
export const useIsWizardView = (): boolean => {
  const router = useRouter();
  const { isAuthorized } = useAuth();
  const pathname = router.pathname;
  const isListingPage = LISTING_VIEW_PATTERN.test(pathname);
  const listingAlias = router.isReady ? (router.query.listingAlias as string) : '';

  const { data: listingDetails } = useGetListingDetails(listingAlias, {
    enabled: isListingPage && Boolean(listingAlias) && isAuthorized,
  });

  if (!pathname) return false;

  if (isListingPage) {
    return listingDetails?.isMultifamily !== true;
  }

  return WIZARD_VIEW_PATTERNS.some((pattern) => pattern.test(pathname));
};
